import { TFunction } from 'i18next';
import { twMerge } from 'tailwind-merge';
import { KpiObjectType } from '@/api/entities/kpi';
import Card from '@/components/Card';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { NotFound } from '@/components/Message/NotFound';
import { PerClassBarRow, KpiModel } from '@/type/kpi-dashboard.types';
import {
  buildAxisTicks,
  buildPerClassBarMetrics,
  formatAnnotationCount,
  getKpiObjectLabels,
  getNiceAxisMax,
  isRareAnnotationCount,
  KPI_TIER_COLORS,
} from '@/utils/kpi-dashboard.utils';
import { PerClassBarChart } from './PerClassBarChart';
import { InfoTip } from '@/components/Help/InfoTip';
import { DASHBOARD_TIPS, getKpiTip } from '@/docs/tips/dashboard';

interface PerClassAnnotationsPanelProps {
  t?: TFunction<undefined>;
  type: KpiObjectType;
  model?: KpiModel;
}

/** Colour of the annotation bars for classes that are not rare. */
const ANNOTATION_BAR_COLOR = '#5433FF';

export const PerClassAnnotationsPanel = ({
  t,
  type,
  model,
}: PerClassAnnotationsPanelProps) => {
  const { object } = getKpiObjectLabels(t, type);

  const maxAnnotations = Math.max(
    ...(model?.classes ?? []).map(({ annotations }) => annotations ?? 0),
  );
  const axisMax = getNiceAxisMax(maxAnnotations);
  const rows: PerClassBarRow[] = [...(model?.classes ?? [])]
    .sort(
      (first, second) => (first?.annotations ?? 0) - (second?.annotations ?? 0),
    )
    .map((kpiClass) => ({
      id: kpiClass?.id,
      label: kpiClass?.name,
      value: kpiClass?.annotations,
      color: isRareAnnotationCount(kpiClass?.annotations, maxAnnotations)
        ? KPI_TIER_COLORS.moderate.solid
        : ANNOTATION_BAR_COLOR,
      metrics: buildPerClassBarMetrics(t, kpiClass),
    }));

  return (
    <Card
      title={
        <TitleSection
          text={
            <span className="inline-flex items-center gap-1.5">
              {t('perObjectImageAnnotations', { object })}
              <InfoTip
                tip={getKpiTip(DASHBOARD_TIPS.kpiPerClassAnnotations, type)}
              />
            </span>
          }
          defaultIconClassName="ki-graph"
        />
      }
      bodyClassName={twMerge('pt-5 pb-8 flex flex-col gap-5', !model && 'pt-8')}
    >
      {!model ? (
        <NotFound text={`${t('common:noDataFound')}...`} />
      ) : (
        <>
          <p className="text-sm font-medium text-slate-600">
            {t('sortedAscendingRarestFirst')}
          </p>
          <PerClassBarChart
            rows={rows}
            axisMax={axisMax}
            ticks={buildAxisTicks(axisMax)}
            formatValue={formatAnnotationCount}
          />
        </>
      )}
    </Card>
  );
};
